import Navbar from '../components/Navbar';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-[#F2F7F4] texture-grain">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-[#2D6A4F] text-sm font-medium mb-6 hover:underline">
          <ArrowLeft size={14} /> Back
        </Link>

        <div className="bg-white rounded-[2rem] p-8 sm:p-12 shadow-[0_8px_30px_rgb(27,67,50,0.08)]" data-testid="privacy-page">
          <h1 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold text-[#1B4332] mb-2">Privacy Policy</h1>
          <p className="text-sm text-[#4B6B58] mb-8">Last updated: March 24, 2026</p>

          <div className="prose prose-sm max-w-none text-[#122A1F] space-y-6">
            <section>
              <h2 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332] mb-2">1. Introduction</h2>
              <p className="text-sm leading-relaxed text-[#4B6B58]">
                The New Initiative Community Enhancement Foundation (The N.I.C.E. Foundation), a 501(c)(3) nonprofit organization (EIN: 41-4838456), doing business as The Oaks Legacy Ranch, operates Stable Solutions ("the Service") at www.stablesolutions-theoakslegacyranch.com. This Privacy Policy describes how we collect, use, and protect your personal information. We are committed to protecting your privacy and handling your data responsibly.
              </p>
            </section>

            <section>
              <h2 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332] mb-2">2. Information We Collect</h2>
              <p className="text-sm leading-relaxed text-[#4B6B58] mb-2"><strong>Account Information:</strong></p>
              <ul className="list-disc list-inside text-sm text-[#4B6B58] space-y-1">
                <li>Name, email address, and password (encrypted) when you create an account</li>
                <li>Payment information processed securely through Stripe (we do not store credit card numbers)</li>
              </ul>
              <p className="text-sm leading-relaxed text-[#4B6B58] mt-3 mb-2"><strong>Horse & Barn Data:</strong></p>
              <ul className="list-disc list-inside text-sm text-[#4B6B58] space-y-1">
                <li>Horse profiles (name, breed, date of birth, registration, etc.)</li>
                <li>Health records (vaccinations, vet visits, medications, farrier, etc.)</li>
                <li>Photos uploaded to horse profiles and galleries</li>
                <li>Barn management data (chores, feed schedules, turnout, etc.)</li>
                <li>Training, breeding, show, and expense records</li>
              </ul>
              <p className="text-sm leading-relaxed text-[#4B6B58] mt-3 mb-2"><strong>Community Data:</strong></p>
              <ul className="list-disc list-inside text-sm text-[#4B6B58] space-y-1">
                <li>Chat messages sent through the subscriber messaging system</li>
                <li>Marketplace listings you create</li>
                <li>Community events you post or interact with</li>
                <li>Emergency contacts you add</li>
              </ul>
              <p className="text-sm leading-relaxed text-[#4B6B58] mt-3 mb-2"><strong>Usage Data:</strong></p>
              <ul className="list-disc list-inside text-sm text-[#4B6B58] space-y-1">
                <li>Log data such as access times and pages viewed</li>
                <li>Device and browser information</li>
              </ul>
            </section>

            <section>
              <h2 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332] mb-2">3. How We Use Your Information</h2>
              <ul className="list-disc list-inside text-sm text-[#4B6B58] space-y-1">
                <li>To provide, maintain, and improve the Service</li>
                <li>To process subscription payments</li>
                <li>To generate AI-powered health insights for your horses (your data is sent to AI providers for analysis; it is not stored by them)</li>
                <li>To enable features like horse card sharing, community chat, and the marketplace</li>
                <li>To send important service-related communications</li>
                <li>To support the mission of The N.I.C.E. Foundation through subscription revenue</li>
              </ul>
            </section>

            <section>
              <h2 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332] mb-2">4. How We Protect Your Information</h2>
              <p className="text-sm leading-relaxed text-[#4B6B58]">
                We take the security of your data seriously and implement appropriate technical and organizational measures to protect it:
              </p>
              <ul className="list-disc list-inside text-sm text-[#4B6B58] mt-2 space-y-1">
                <li>Passwords are encrypted using industry-standard hashing (bcrypt)</li>
                <li>All data transmission is encrypted via HTTPS/TLS</li>
                <li>Payment processing is handled by Stripe, a PCI-DSS compliant payment processor</li>
                <li>Photos and files are stored in secure cloud storage with access controls</li>
                <li>Database access is restricted and monitored</li>
              </ul>
            </section>

            <section>
              <h2 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332] mb-2">5. Data Sharing & Third Parties</h2>
              <p className="text-sm leading-relaxed text-[#4B6B58]">
                We do not sell your personal information. We share data only in the following circumstances:
              </p>
              <ul className="list-disc list-inside text-sm text-[#4B6B58] mt-2 space-y-1">
                <li><strong>Horse Card Sharing:</strong> When you share a horse card, the recipient can view your horse's records and add new ones. You control who has access and can revoke it at any time.</li>
                <li><strong>Service Providers:</strong> We use third-party services (Stripe for payments, cloud providers for hosting and storage, AI providers for health insights) that process data on our behalf under strict data protection agreements.</li>
                <li><strong>Community Features:</strong> Information you post in chat, the marketplace, or the events board is visible to other subscribers as intended by those features.</li>
                <li><strong>Legal Requirements:</strong> We may disclose information if required by law, court order, or governmental regulation.</li>
              </ul>
            </section>

            <section>
              <h2 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332] mb-2">6. Your Data Rights</h2>
              <p className="text-sm leading-relaxed text-[#4B6B58]">You have the right to:</p>
              <ul className="list-disc list-inside text-sm text-[#4B6B58] mt-2 space-y-1">
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                <li><strong>Deletion:</strong> Request deletion of your account and associated data</li>
                <li><strong>Export:</strong> Download your records in PDF format</li>
                <li><strong>Revoke Sharing:</strong> Remove shared access to your horse cards at any time</li>
              </ul>
              <p className="text-sm leading-relaxed text-[#4B6B58] mt-2">
                To exercise these rights, please contact us using the information provided below.
              </p>
            </section>

            <section>
              <h2 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332] mb-2">7. Data Retention</h2>
              <p className="text-sm leading-relaxed text-[#4B6B58]">
                We retain your data for as long as your account is active or as needed to provide the Service. If you cancel your subscription, your data remains in read-only mode and is preserved. If you request account deletion, we will remove your personal data within 30 days, except where retention is required by law.
              </p>
            </section>

            <section>
              <h2 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332] mb-2">8. Children's Privacy</h2>
              <p className="text-sm leading-relaxed text-[#4B6B58]">
                The Service is not intended for use by individuals under the age of 18. We do not knowingly collect personal information from children. If we learn that we have collected data from a child under 18, we will take steps to delete it promptly.
              </p>
            </section>

            <section>
              <h2 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332] mb-2">9. Cookies & Tracking</h2>
              <p className="text-sm leading-relaxed text-[#4B6B58]">
                The Service uses essential cookies and local storage to maintain your login session and preferences. We do not use third-party tracking cookies or advertising trackers. We do not sell or share your browsing data with advertisers.
              </p>
            </section>

            <section>
              <h2 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332] mb-2">10. Changes to This Policy</h2>
              <p className="text-sm leading-relaxed text-[#4B6B58]">
                We may update this Privacy Policy from time to time. Material changes will be communicated through the Service or via email. Your continued use of the Service after changes constitutes acceptance of the updated policy.
              </p>
            </section>

            <section>
              <h2 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332] mb-2">11. Nonprofit Disclosure</h2>
              <p className="text-sm leading-relaxed text-[#4B6B58]">
                Stable Solutions is a product of The Oaks Legacy Ranch, a program operating under The New Initiative Community Enhancement Foundation (The N.I.C.E. Foundation), a 501(c)(3) tax-exempt nonprofit organization. Revenue generated through subscriptions supports the charitable programs and mission of the Foundation.
              </p>
            </section>

            <section>
              <h2 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332] mb-2">12. Contact Us</h2>
              <p className="text-sm leading-relaxed text-[#4B6B58]">
                For questions or concerns about this Privacy Policy or your data, please contact:<br /><br />
                <strong>The New Initiative Community Enhancement Foundation</strong><br />
                d/b/a The Oaks Legacy Ranch<br />
                Yamhill, OR 97148<br />
                EIN: 41-4838456<br />
                Oregon Registry: 252577391
              </p>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
}
